import { rufe } from './client';
/* ANG-43: Der Begriff steht einmal — im Angebot, wo er entsteht. */
export { VERTRAGSARTNAME, vertragsartenZurKundenart } from './angebote';
import type {
  Belegkondition,
  Herleitungszeile,
  Positionsart,
  Preisfuehrung,
  Vertragsart,
  Summenblock,
} from './angebote';

/**
 * Der Zugang zum Auftragsmanagement (Lastenheft Kapitel 7).
 *
 * BETRAEGE SIND ZEICHENKETTEN und DIE SUMMEN KOMMEN VOM SERVER — dieselben
 * beiden Regeln wie im Angebot, aus denselben Gruenden (RND-01; eine
 * Zwischensumme im Browser waere die zweite Wahrheit).
 *
 * Die Positions- und Summentypen werden AUS DEM ANGEBOT UEBERNOMMEN und
 * nicht neu geschrieben: AUF-03 verlangt die Preisherleitung "nach derselben
 * Regel wie alle Belegpositionen", und zwei Abschriften derselben Form waeren
 * zwei Gelegenheiten, sie auseinanderlaufen zu lassen.
 */
export type { Belegkondition, Herleitungszeile, Positionsart, Preisfuehrung, Summenblock };

/** AUF-12, Achse 0: der Lebenslauf. */
export type Auftragsstatus = 'entwurf' | 'bestaetigt' | 'storniert';

export const STATUSNAME: Readonly<Record<Auftragsstatus, string>> = {
  entwurf: 'Entwurf',
  bestaetigt: 'bestätigt',
  storniert: 'storniert',
};

/** AUF-12, Achse 1 und 2. Sie werden fortgeschrieben, nicht gesetzt (AUF-13). */
export type Erbringung = 'offen' | 'teilweise' | 'erbracht';
export type Fakturierung = 'offen' | 'teilweise' | 'berechnet';

export const ERBRINGUNGSNAME: Readonly<Record<Erbringung, string>> = {
  offen: 'offen',
  teilweise: 'teilweise erbracht',
  erbracht: 'erbracht',
};

export const FAKTURIERUNGSNAME: Readonly<Record<Fakturierung, string>> = {
  offen: 'offen',
  teilweise: 'teilweise berechnet',
  berechnet: 'berechnet',
};

export type Auftragsherkunft = 'angebot' | 'telefon' | 'innendienst' | 'portal' | 'checkin';

export const HERKUNFTSNAME: Readonly<Record<Auftragsherkunft, string>> = {
  angebot: 'aus Angebot',
  telefon: 'telefonisch',
  innendienst: 'Innendienst',
  portal: 'Buchungsstrecke',
  checkin: 'Check-in vor Ort',
};

export type Fakturierungszeitpunkt = 'bei_buchung' | 'nach_durchfuehrung' | 'vereinbart';

export const ZEITPUNKTNAME: Readonly<Record<Fakturierungszeitpunkt, string>> = {
  bei_buchung: 'bei Buchung',
  nach_durchfuehrung: 'nach Durchführung',
  vereinbart: 'zum vereinbarten Termin',
};

export type Vertriebsform =
  | 'einzelplatz'
  | 'exklusivtermin'
  | 'baustein'
  | 'ressource'
  | 'zusatzleistung';

/** AUF-05: worauf sich die Leistung bezieht. */
export type Bezugsart = 'termin' | 'teilnehmer' | 'ressource' | 'zeitraum';

export const BEZUGSNAME: Readonly<Record<Bezugsart, string>> = {
  termin: 'Termin',
  teilnehmer: 'Teilnehmer',
  ressource: 'Ressource',
  zeitraum: 'Zeitraum',
};

/**
 * AUF-04: die sieben Woerter, in denen der Anwender denkt — dazu die drei
 * Gliederungsarten aus ANG-02. Der Server LEITET sie ab; die Oberflaeche
 * zeigt an, was sie bekommt, und rechnet sie nicht selbst aus.
 */
export type Auf04Positionsart =
  | 'teilnehmerplatz'
  | 'exklusivtermin'
  | 'zusatzleistung'
  | 'baustein'
  | 'ressource'
  | 'verpflegung'
  | 'freitext'
  | 'text'
  | 'zwischensumme'
  | 'rabatt';

export const POSITIONSARTNAME: Readonly<Record<Auf04Positionsart, string>> = {
  teilnehmerplatz: 'Teilnehmerplatz',
  exklusivtermin: 'Exklusivtermin',
  zusatzleistung: 'Zusatzleistung',
  baustein: 'Baustein',
  ressource: 'Ressource',
  verpflegung: 'Verpflegung',
  freitext: 'Freitext',
  text: 'Textzeile',
  zwischensumme: 'Zwischensumme',
  rabatt: 'Rabattzeile',
};

export type Auftragsposition = {
  readonly id: string;
  readonly reihenfolge: number;
  readonly abschnitt: number;
  readonly art: Positionsart;
  readonly vertriebsform: Vertriebsform | null;
  readonly positionsart: Auf04Positionsart;
  readonly artikel: string | null;
  readonly artikelnummer: string | null;
  readonly bezeichnung: string;
  readonly beschreibung: string | null;
  readonly menge: string | null;
  readonly mengeneinheit: string | null;
  readonly einzelpreis: string | null;
  readonly steuerschluessel: string | null;
  readonly steuerProzent: string | null;
  readonly preisUebersteuert: boolean;
  /**
   * PRD-12: ob der eingefrorene Einzelpreis die Steuer schon enthält.
   *
   * Der Server liefert es seit Migration 0109; der Beleg rechnet damit —
   * ein brutto gepflegter Preis wird auf einem Nettobeleg zurückgerechnet.
   * NULL bei Bestandszeilen: dort wird nicht umgerechnet.
   */
  readonly istBrutto: boolean | null;
  readonly preisherkunft: string | null;
  readonly rabattProzent: string | null;
  readonly konditionen: readonly Belegkondition[];
  readonly hoechstrabattProzent: string | null;
  readonly herleitung: readonly Herleitungszeile[];
  readonly bezugsart: Bezugsart | null;
  readonly bezugId: string | null;
  readonly bezugVon: string | null;
  readonly bezugBis: string | null;
  readonly erbrachteMenge: string;
  readonly berechneteMenge: string;
  readonly berechneterWert: string;
  readonly offeneMenge: string | null;
  readonly storniertAm: string | null;
  readonly stornogrund: string | null;
  readonly angebotsposition: string | null;
  readonly zeilenbrutto: string | null;
  readonly netto: string | null;
};

export type Auftragszeile = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly auftragsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly status: Auftragsstatus;
  readonly erbringung: Erbringung;
  readonly fakturierung: Fakturierung;
  readonly herkunft: Auftragsherkunft;
  readonly bearbeiterName: string | null;
  readonly eingefroren: boolean;
  readonly belegbrutto: string;
  readonly positionen: number;
};

export type Auftrag = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly auftragsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly kundenart: string;
  readonly besteller: string | null;
  readonly bestellerName: string | null;
  readonly rechnungsempfaenger: string | null;
  readonly rechnungsempfaengerName: string | null;
  readonly bearbeiter: string | null;
  readonly bearbeiterName: string | null;
  readonly belegdatum: string;
  readonly betreff: string;
  readonly bestellreferenz: string | null;
  readonly kopftext: string | null;
  readonly fusstext: string | null;
  readonly preisfuehrung: Preisfuehrung;
  /** ANG-43: bestimmt die Storno- und Umbuchungsstaffel (BUC-45). */
  readonly vertragsart: Vertragsart;
  /** PAR-03: wer den Beleg vermittelt hat — Autohaus, Fahrschule, Verband. */
  readonly vermittler: string | null;
  readonly vermittlername: string | null;
  readonly vermittlercode: string | null;
  readonly herkunft: Auftragsherkunft;
  readonly zahlungsziel: number;
  readonly skontoProzent: string | null;
  readonly skontoTage: number | null;
  readonly fakturierungszeitpunkt: Fakturierungszeitpunkt;
  readonly fakturierungsdatum: string | null;
  readonly status: Auftragsstatus;
  readonly erbringung: Erbringung;
  readonly fakturierung: Fakturierung;
  readonly uebersteuert: boolean;
  readonly uebersteuerungsgrund: string | null;
  readonly bestaetigtAm: string | null;
  readonly storniertAm: string | null;
  readonly stornogrund: string | null;
  readonly eingefroren: boolean;
  /**
   * AUF-14: Der Kunde hat diese Fassung — mit der Bestätigung oder mit der
   * erzeugten Auftragsbestätigung. Der VERTRAG steht damit; Erbringung,
   * Fakturierung und Storno laufen weiter (AUF-12, AUF-13, AUF-15).
   *
   * Nicht dasselbe wie `eingefroren`: Das heißt „es gibt eine neuere
   * Fassung" und trifft die abgelöste, nicht die geltende.
   */
  readonly festgeschrieben: boolean;
  readonly angebot: string | null;
  readonly angebotsnummer: string | null;
  readonly vorgaenger: string | null;
  readonly geloescht: boolean;
  readonly angelegtAm: string;
  readonly stand: string;
  readonly positionen: readonly Auftragsposition[];
  readonly summen: Summenblock;
  readonly versionen: readonly {
    readonly id: string;
    readonly version: number;
    readonly status: Auftragsstatus;
    readonly eingefroren: boolean;
    readonly angelegtAm: string;
  }[];
  readonly moeglicheStatus: readonly Auftragsstatus[];
};

export type Fakturierungsregel = {
  readonly vertriebsform: Vertriebsform;
  readonly kundenart: string;
  readonly zeitpunkt: Fakturierungszeitpunkt;
};

/* ── Auftraege ───────────────────────────────────────────────────────────── */

export async function liste(filter: {
  readonly kunde?: string;
  readonly status?: Auftragsstatus | '';
  readonly fakturierung?: Fakturierung | '';
  readonly suche?: string;
  readonly alleVersionen?: boolean;
}): Promise<readonly Auftragszeile[]> {
  const teile = new URLSearchParams();
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  if (filter.status !== undefined && filter.status !== '') teile.set('status', filter.status);
  if (filter.fakturierung !== undefined && filter.fakturierung !== '') {
    teile.set('fakturierung', filter.fakturierung);
  }
  if (filter.suche !== undefined && filter.suche !== '') teile.set('suche', filter.suche);
  if (filter.alleVersionen === true) teile.set('alleVersionen', 'true');
  const frage = teile.toString();
  return rufe(`/auftraege${frage === '' ? '' : `?${frage}`}`);
}

export async function akte(id: string): Promise<Auftrag> {
  return rufe(`/auftraege/${id}`);
}

export async function fakturierungsregeln(): Promise<readonly Fakturierungsregel[]> {
  return rufe('/auftraege/fakturierungsregeln');
}

export async function anlegen(eingabe: {
  readonly kunde: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly besteller?: string | null;
  readonly rechnungsempfaenger?: string | null;
  readonly bestellreferenz?: string | null;
  readonly kopftext?: string | null;
  readonly fusstext?: string | null;
  readonly herkunft?: 'telefon' | 'innendienst' | 'portal' | 'checkin';
  readonly fakturierungszeitpunkt?: Fakturierungszeitpunkt | null;
  readonly fakturierungsdatum?: string | null;
}): Promise<{ readonly id: string; readonly auftragsnummer: string }> {
  return rufe('/auftraege', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * ANG-18 und ANG-19: aus dem angenommenen Angebot wird der Auftrag.
 *
 * `positionen` leer heisst "alles Wirksame". Eine Auswahl uebernimmt genau
 * diese Zeilen; der Rest bleibt am Angebot offen.
 */
export async function ausAngebot(eingabe: {
  readonly angebot: string;
  readonly positionen?: readonly string[];
  readonly bestellreferenz?: string | null;
}): Promise<{ readonly id: string; readonly auftragsnummer: string }> {
  return rufe('/auftraege/aus-angebot', { verfahren: 'POST', rumpf: eingabe });
}

export async function feldSichern(
  id: string,
  feld: string,
  wert: string | number | null,
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/auftraege/${id}`, { verfahren: 'PATCH', rumpf: { stand, [feld]: wert } });
}

/**
 * Die Antwort auf eine neu angelegte Position.
 *
 * `auftrag` steht nur dann darin, wenn der Server die FASSUNG GEWECHSELT hat:
 * Eine Fassung, die aus einem Angebot entstanden ist, wird nicht
 * ueberschrieben, sondern abgeloest (AUF-14). Die Maske muss dann auf die
 * neue Kennung wechseln — sonst zeigt sie eine Fassung, in der ihre eigene
 * Zeile nicht steht.
 */
export type Positionsantwort = {
  readonly id: string;
  readonly auftrag?: { readonly id: string; readonly version: number };
};

export async function positionAnlegen(
  auftrag: string,
  eingabe: {
    readonly art: Positionsart;
    readonly abschnitt?: number;
    readonly artikel?: string | null;
    readonly bezeichnung?: string | null;
    readonly beschreibung?: string | null;
    readonly menge?: number | null;
    readonly einzelpreis?: string | null;
    readonly steuerschluessel?: string | null;
    readonly steuerProzent?: string | null;
    readonly rabattProzent?: string | null;
    readonly vertriebsform?: Vertriebsform;
    readonly bezugsart?: Bezugsart | null;
    readonly bezugId?: string | null;
    readonly bezugVon?: string | null;
    readonly bezugBis?: string | null;
    /**
     * BUC-76: bis wann die Plätze zurückgelegt werden — als Tag (JJJJ-MM-TT)
     * oder Zeitpunkt. FEHLT SIE, WIRD NICHTS GEHALTEN: Wer keine Frist nennt,
     * will keine Option. Ein reiner Tag meint das Ende dieses Tages.
     */
    readonly optionsfrist?: string;
  },
): Promise<Positionsantwort> {
  return rufe(`/auftraege/${auftrag}/positionen`, { verfahren: 'POST', rumpf: eingabe });
}

/**
 * L5 (CRM-116), AUF-17, BUC-76: den Termin einer Einzelplatzzeile im Entwurf
 * wählen oder wechseln. Die alte Option geht zurück; mit Frist wird am neuen
 * Termin gehalten (ohne neue Frist gilt die alte). `null` lässt ihn offen.
 */
export async function positionTerminSetzen(
  auftrag: string,
  position: string,
  eingabe: { readonly termin: string | null; readonly optionsfrist?: string },
): Promise<void> {
  await rufe(`/auftraege/${auftrag}/positionen/${position}/termin`, {
    verfahren: 'PUT',
    rumpf: eingabe,
  });
}

export async function positionLoeschen(auftrag: string, position: string): Promise<void> {
  await rufe(`/auftraege/${auftrag}/positionen/${position}`, { verfahren: 'DELETE' });
}

/** BUC-51, C3.4: einzelne Plätze einer Zeile und, auf Wunsch, wer geht. */
export type Platzauswahl = {
  readonly plaetze: number;
  readonly teilnehmer?: readonly string[];
};

/** BUC-144 (AeA-59): wie die Erklärung einging, mit freiem Verweis. */
export type Erklaerung = {
  readonly erklaerungsweg?: string;
  readonly erklaerungsverweis?: string;
};

/** C7.5, BUC-45: was der Storno einer Zeile kosten würde. */
export async function positionStornovorschau(
  auftrag: string,
  position: string,
  /** C7.6, BUC-144: der Zugang der Erklärung, als Tag (JJJJ-MM-TT). */
  zugangAm?: string,
  teil?: Platzauswahl,
): Promise<Stornoposten> {
  const abfrage = new URLSearchParams();
  if (zugangAm !== undefined) abfrage.set('zugangAm', zugangAm);
  if (teil !== undefined) {
    abfrage.set('plaetze', String(teil.plaetze));
    if (teil.teilnehmer !== undefined && teil.teilnehmer.length > 0) {
      abfrage.set('teilnehmer', teil.teilnehmer.join(','));
    }
  }
  const pfad = `/auftraege/${auftrag}/positionen/${position}/stornovorschau`;
  return rufe(abfrage.size === 0 ? pfad : `${pfad}?${abfrage.toString()}`);
}

/**
 * AUF-15: Teilstorno. Die Zeile bleibt mit ihrem Grund stehen; mit `teil`
 * gehen nur einzelne Plätze, und die Zeile bleibt wirksam (C3.4).
 */
export async function positionStornieren(
  auftrag: string,
  position: string,
  grund: string,
  /** C7.6, BUC-144: wie in der Vorschau, damit dieselbe Stufe greift. */
  zugangAm?: string,
  erklaerung: Erklaerung = {},
  teil?: Platzauswahl,
): Promise<void> {
  await rufe(`/auftraege/${auftrag}/positionen/${position}/storno`, {
    verfahren: 'POST',
    rumpf: {
      grund,
      ...(zugangAm === undefined ? {} : { zugangAm }),
      ...erklaerung,
      ...(teil === undefined
        ? {}
        : {
            plaetze: teil.plaetze,
            ...(teil.teilnehmer !== undefined && teil.teilnehmer.length > 0
              ? { teilnehmer: teil.teilnehmer }
              : {}),
          }),
    },
  });
}

/** Buchung, Plätze und Teilnehmer einer Zeile — zur Auswahl beim Teilstorno. */
export type Positionsteilnehmer = {
  readonly buchung: string | null;
  readonly plaetze: number;
  readonly teilnehmer: readonly { readonly id: string; readonly position: number; readonly name: string | null }[];
};

export async function positionsteilnehmer(
  auftrag: string,
  position: string,
): Promise<Positionsteilnehmer> {
  return rufe(`/auftraege/${auftrag}/positionen/${position}/teilnehmer`);
}

/** AUF-06, BUC-50 (CRM-118, L7): was am Auftrag hängt, über alle Fassungen. */
export type Auftragsverbindungen = {
  readonly rechnungen: readonly {
    readonly id: string;
    readonly rechnungsnummer: string | null;
    readonly belegart: string;
    readonly status: string;
    readonly belegdatum: string;
  }[];
  readonly buchungen: readonly {
    readonly buchungsnummer: string;
    readonly status: string;
    readonly termin: string;
  }[];
  readonly termine: readonly { readonly id: string; readonly beginn: string; readonly bezeichnung: string }[];
  readonly rahmen: { readonly id: string; readonly rahmennummer: string } | null;
};

export async function verbunden(auftrag: string): Promise<Auftragsverbindungen> {
  return rufe(`/auftraege/${auftrag}/verbunden`);
}

/** BUC-145, BUC-146, BUC-148: ein Stornovorgang des Auftrags, auch zurückgenommen. */
export type Auftragsvorgang = {
  readonly id: string;
  readonly plaetze: number;
  readonly position: string | null;
  readonly bezeichnung: string | null;
  readonly buchungsnummer: string | null;
  readonly zugangAm: string;
  readonly erfasstAm: string;
  readonly gebuehr: string;
  readonly herabgesetztAuf: string | null;
  readonly herabsetzungGrund: string | null;
  readonly zuErstatten: string;
  readonly zuBerechnen: string;
  readonly zurueckgenommenAm: string | null;
  readonly erklaerungsweg: string | null;
  readonly erklaerungsverweis: string | null;
  readonly grund: string | null;
  readonly beleg: string | null;
  readonly rechnungsnummer: string | null;
  readonly belegstatus: string | null;
  /** Mit diesem Vorgang fiel die ganze Zeile weg — zurück nur über die Zeile. */
  readonly positionStorniert: boolean;
};

export async function stornovorgaenge(auftrag: string): Promise<readonly Auftragsvorgang[]> {
  const { vorgaenge } = await rufe<{ vorgaenge: readonly Auftragsvorgang[] }>(
    `/auftraege/${auftrag}/stornovorgaenge`,
  );
  return vorgaenge;
}

/** BUC-148 (AeA-58): den Storno einzelner Plätze zurücknehmen. */
export async function vorgangZuruecknehmen(
  auftrag: string,
  vorgang: string,
  grund: string,
): Promise<void> {
  await rufe(`/auftraege/${auftrag}/stornovorgaenge/${vorgang}/ruecknahme`, {
    verfahren: 'POST',
    rumpf: { grund },
  });
}

/** BUC-148 (AeA-58): den Storno des ganzen Auftrags zurücknehmen. */
export async function stornoZuruecknehmen(auftrag: string, grund: string): Promise<void> {
  await rufe(`/auftraege/${auftrag}/storno/ruecknahme`, { verfahren: 'POST', rumpf: { grund } });
}

/** BUC-148 (AeA-58): den Storno einer ganzen Zeile zurücknehmen. */
export async function positionStornoZuruecknehmen(
  auftrag: string,
  position: string,
  grund: string,
): Promise<void> {
  await rufe(`/auftraege/${auftrag}/positionen/${position}/storno/ruecknahme`, {
    verfahren: 'POST',
    rumpf: { grund },
  });
}

export async function neuOrdnen(auftrag: string, reihenfolge: readonly string[]): Promise<void> {
  await rufe(`/auftraege/${auftrag}/reihenfolge`, { verfahren: 'PUT', rumpf: { reihenfolge } });
}

export async function statusSetzen(
  auftrag: string,
  status: Auftragsstatus,
  stornogrund: string | null = null,
  /** C7.6, BUC-144: der Zugang der Erklärung, als Tag (JJJJ-MM-TT). */
  zugangAm?: string,
  erklaerung: Erklaerung = {},
): Promise<void> {
  await rufe(`/auftraege/${auftrag}/status`, {
    verfahren: 'PUT',
    rumpf: {
      status,
      stornogrund,
      ...(zugangAm === undefined ? {} : { zugangAm }),
      ...erklaerung,
    },
  });
}

/* ── Der Storno und seine Gebuehren (BUC-45, BUC-145, AUF-15) ─────────────── */

export type Stornoposten = {
  readonly position: string;
  readonly bezeichnung: string;
  /** Mit Buchung läuft der Vorgang über sie — der Platz wird frei. */
  readonly mitBuchung: boolean;
  readonly staffel: string;
  readonly abstandInTagen: number;
  /** FRS-19: Beginn des Termins, von dem die Frist läuft; `null` ohne Termin. */
  readonly terminbeginn: string | null;
  /** `null`, wenn die Staffel für diesen Abstand keine Stufe kennt. */
  readonly satzProzent: string | null;
  /**
   * AUF-15: warum die Zeile ohne Gebühr wegfällt — Entwurf, keine Stufe der
   * Staffel, kein Tag, von dem zu rechnen wäre. `null`: nach Staffel gerechnet.
   */
  readonly ohneGebuehr: 'entwurf' | 'keine_stufe' | 'keine_staffel' | 'kein_tag' | null;
  /** Bei mehreren Buchungen an einer Zeile: Staffel und Satz je Buchung. */
  readonly teile?: readonly Stornoteil[];
  readonly grundbetrag: string;
  readonly gebuehr: string;
  readonly verrechnet: string;
  readonly zuBerechnen: string;
  readonly zuErstatten: string;
  readonly hinweise: readonly string[];
};

export type Stornoteil = {
  readonly buchung: string;
  readonly staffel: string;
  readonly abstandInTagen: number;
  readonly satzProzent: string | null;
  readonly grundbetrag: string;
  readonly gebuehr: string;
  readonly ohneGebuehr: 'keine_stufe' | 'keine_staffel' | null;
};

export type Stornovorschau = {
  readonly zugangAm: string;
  /** AUF-14, AUF-16: was den Storno verhindert, bevor jemand ihn versucht. */
  readonly sperre: { readonly kennung: string; readonly meldung: string } | null;
  readonly posten: readonly Stornoposten[];
  readonly gebuehr: string;
  readonly verrechnet: string;
  readonly zuBerechnen: string;
  readonly zuErstatten: string;
};

/**
 * BUC-45: „Die Berechnung wird VOR DER AUSFUEHRUNG angezeigt."
 *
 * Sie aendert nichts — weder Status noch Platz noch Vorgang. Wer am Telefon
 * sagen soll, was ein Ruecktritt kostet, ruft sie auf und storniert nicht.
 */
export async function stornovorschau(auftrag: string, zugangAm?: string): Promise<Stornovorschau> {
  return rufe(
    zugangAm === undefined
      ? `/auftraege/${auftrag}/stornovorschau`
      : `/auftraege/${auftrag}/stornovorschau?zugangAm=${encodeURIComponent(zugangAm)}`,
  );
}

/** AUF-13: die Uebersteuerung. `grund: null` hebt sie auf. */
export async function achsenSetzen(
  auftrag: string,
  werte: {
    readonly erbringung: Erbringung;
    readonly fakturierung: Fakturierung;
    readonly grund: string | null;
  },
): Promise<void> {
  await rufe(`/auftraege/${auftrag}/achsen`, { verfahren: 'PUT', rumpf: werte });
}

/** AUF-14: eine neue Fassung. Die bisherige wird dabei eingefroren. */
export async function neueVersion(
  auftrag: string,
): Promise<{ readonly id: string; readonly version: number }> {
  return rufe(`/auftraege/${auftrag}/versionen`, { verfahren: 'POST' });
}

export async function loeschen(auftrag: string): Promise<void> {
  await rufe(`/auftraege/${auftrag}`, { verfahren: 'DELETE' });
}

/* ── Anzahlungen und Abschlaege (AUF-22) ──────────────────────────────────── */

export type Anzahlungsart = 'anzahlung' | 'abschlag';

export const ANZAHLUNGSNAME: Readonly<Record<Anzahlungsart, string>> = {
  anzahlung: 'Anzahlung',
  abschlag: 'Abschlag',
};

export type Anzahlung = {
  readonly id: string;
  readonly art: Anzahlungsart;
  readonly bezeichnung: string;
  readonly reihenfolge: number;
  readonly betrag: string | null;
  readonly prozentsatz: string | null;
  readonly steuerschluessel: string | null;
  readonly faelligAm: string;
  readonly bemerkung: string | null;
  readonly berechnetAm: string | null;
  readonly bezahltAm: string | null;
  readonly abgesetztAm: string | null;
  /** AUF-22, RNG-09: beim Eingang festgehalten (0346); `null`, solange offen. */
  readonly gezahlterBetrag: string | null;
  /** Der auf den Beleg angewandte Betrag — nach dem Eingang der gezahlte. */
  readonly wirksamerBetrag: string;
};

/* ── Abweichungen (AUF-23) ────────────────────────────────────────────────── */

export type Abweichungsart =
  | 'nichterscheinen'
  | 'teilstorno'
  | 'zusatzgruppe'
  | 'zusatzleistung_vor_ort';

export const ABWEICHUNGSNAME: Readonly<Record<Abweichungsart, string>> = {
  nichterscheinen: 'Nichterscheinen',
  teilstorno: 'Teilstorno',
  zusatzgruppe: 'zusätzliche Gruppe',
  zusatzleistung_vor_ort: 'Zusatzleistung vor Ort',
};

export type Abweichungswirkung = 'berechnen' | 'nicht_berechnen' | 'gutschreiben';

export const WIRKUNGSNAME: Readonly<Record<Abweichungswirkung, string>> = {
  berechnen: 'wird berechnet',
  nicht_berechnen: 'wird nicht berechnet',
  gutschreiben: 'wird gutgeschrieben',
};

export type Abweichung = {
  readonly id: string;
  readonly position: string | null;
  readonly positionsbezeichnung: string | null;
  readonly art: Abweichungsart;
  readonly festgestelltAm: string;
  readonly menge: string | null;
  readonly bemerkung: string;
  readonly wirkung: Abweichungswirkung;
  readonly betrag: string | null;
  readonly erzeugtePosition: string | null;
  readonly erfasstVonName: string | null;
  readonly angelegtAm: string;
};

/* ── Aenderungshistorie (AUF-18) ──────────────────────────────────────────── */

export type Historienzeile = {
  readonly zeitpunkt: string;
  readonly benutzer: string | null;
  readonly vorgang: string | null;
  readonly gegenstand: string;
  readonly operation: 'INSERT' | 'UPDATE' | 'DELETE';
  readonly bezeichnung: string | null;
  readonly felder: readonly string[] | null;
  readonly positionswertDavor: string | null;
  readonly positionswertDanach: string | null;
  readonly positionswertDifferenz: string | null;
};

/* ── Fakturierungsliste (AUF-20) und Uebersicht (AUF-28) ──────────────────── */

export type Fakturierungszeile = {
  readonly auftrag: string;
  readonly auftragsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly positionId: string;
  readonly bezeichnung: string;
  readonly offeneMenge: string;
  readonly offenerWert: string;
  readonly zeitpunkt: Fakturierungszeitpunkt;
  readonly faelligAm: string | null;
  readonly ueberfaellig: boolean;
};

export type Uebersichtszeile = {
  readonly auftrag: string;
  readonly auftragsnummer: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly status: Auftragsstatus;
  readonly erbringung: Erbringung;
  readonly fakturierung: Fakturierung;
  readonly version: number;
  readonly offenerWert: string;
  readonly offenePositionen: number;
};

/* ── Zugriffe ────────────────────────────────────────────────────────────── */

export async function anzahlungen(auftrag: string): Promise<readonly Anzahlung[]> {
  return rufe(`/auftraege/${auftrag}/anzahlungen`);
}

export async function anzahlungAnlegen(
  auftrag: string,
  eingabe: {
    readonly art?: Anzahlungsart;
    readonly bezeichnung: string;
    readonly betrag?: string | null;
    readonly prozentsatz?: string | null;
    readonly steuerschluessel?: string | null;
    readonly faelligAm: string;
    readonly bemerkung?: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/auftraege/${auftrag}/anzahlungen`, { verfahren: 'POST', rumpf: eingabe });
}

/** C7.1: den Zahlungseingang einer Anzahlung vermerken (RNG-09). */
export async function anzahlungEingang(
  auftrag: string,
  anzahlung: string,
  bezahltAm: string,
  /** AUF-22: nur, wenn der Kunde weniger als vereinbart gezahlt hat. */
  gezahlterBetrag?: string,
): Promise<readonly Anzahlung[]> {
  return rufe(`/auftraege/${auftrag}/anzahlungen/${anzahlung}/eingang`, {
    verfahren: 'POST',
    rumpf: gezahlterBetrag === undefined ? { bezahltAm } : { bezahltAm, gezahlterBetrag },
  });
}

export async function anzahlungLoeschen(auftrag: string, anzahlung: string): Promise<void> {
  await rufe(`/auftraege/${auftrag}/anzahlungen/${anzahlung}`, { verfahren: 'DELETE' });
}

export async function abweichungen(auftrag: string): Promise<readonly Abweichung[]> {
  return rufe(`/auftraege/${auftrag}/abweichungen`);
}

/** `teilstorno` fehlt in der Auswahl: Er entsteht beim Stornieren (AUF-15). */
export async function abweichungErfassen(
  auftrag: string,
  eingabe: {
    readonly position?: string | null;
    readonly art: Exclude<Abweichungsart, 'teilstorno'>;
    readonly festgestelltAm: string;
    readonly menge?: string | null;
    readonly bemerkung: string;
    readonly wirkung: Abweichungswirkung;
    readonly betrag?: string | null;
    readonly artikel?: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/auftraege/${auftrag}/abweichungen`, { verfahren: 'POST', rumpf: eingabe });
}

export async function historie(auftrag: string): Promise<readonly Historienzeile[]> {
  return rufe(`/auftraege/${auftrag}/historie`);
}

export async function fakturierungsliste(filter: {
  readonly stichtag?: string;
  readonly kunde?: string;
} = {}): Promise<readonly Fakturierungszeile[]> {
  const teile = new URLSearchParams();
  if (filter.stichtag !== undefined) teile.set('stichtag', filter.stichtag);
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  const frage = teile.toString();
  return rufe(`/auftraege/fakturierungsliste${frage === '' ? '' : `?${frage}`}`);
}

export async function uebersicht(kunde: string): Promise<readonly Uebersichtszeile[]> {
  return rufe(`/auftraege/uebersicht/${kunde}`);
}
